import React, { useState, useMemo } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useStore, getUserById } from '../store';
import { BountyCard } from '../components/missions/BountyCard';
import { MissionDetailModal } from '../components/missions/MissionDetailModal';
import { AuthModal } from '../components/auth/AuthModal';
import { ReportModal } from '../components/auth/ReportModal';
import { AdSlot } from '../components/ui/AdSlot';
import { Mission, MISSION_CATEGORIES } from '../types';
import { t } from '../lib/i18n';

const SORT_OPTIONS = [
  { value: 'new', label: 'Newest First' },
  { value: 'reward_desc', label: 'Highest Reward' },
  { value: 'reward_asc', label: 'Lowest Reward' },
  { value: 'hours', label: 'Fewest Hours' },
];

export const MissionsPage: React.FC = () => {
  const { missions, lang } = useStore();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('new');
  const [activeTag, setActiveTag] = useState('All');
  const [selectedMission, setSelectedMission] = useState<any>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<any>(null);
  const [index, setIndex] = useState(0);
  const [showAd, setShowAd] = useState(false);

  const filtered = useMemo(() => {
    let list = missions.filter(m => m.status !== 'cancelled');
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(m =>
        m.title.toLowerCase().includes(q) ||
        m.description.toLowerCase().includes(q) ||
        m.skills.some(s => s.toLowerCase().includes(q))
      );
    }
    if (category) list = list.filter(m => m.category === category);
    if (activeTag !== 'All') {
      if (activeTag === 'Remote') list = list.filter(m => m.location.toLowerCase().includes('remote'));
      else list = list.filter(m => m.category === activeTag);
    }
    if (sort === 'new') list = [...list].sort((a, b) => b.createdAt - a.createdAt);
    else if (sort === 'reward_desc') list = [...list].sort((a, b) => b.reward - a.reward);
    else if (sort === 'reward_asc') list = [...list].sort((a, b) => a.reward - b.reward);
    else if (sort === 'hours') list = [...list].sort((a, b) => a.estimatedHours - b.estimatedHours);
    return list;
  }, [missions, query, category, sort, activeTag]);

  const tags = ['All', ...MISSION_CATEGORIES, 'Remote'];

  const goNext = () => {
    if (showAd) {
      setShowAd(false);
      setIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (index < filtered.length - 1) {
      setShowAd(true);
    }
  };
  const goPrev = () => {
    if (showAd) {
      setShowAd(false);
    } else {
      setIndex(i => Math.max(i - 1, 0));
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: goNext,
    onSwipedRight: goPrev,
    trackMouse: true,
  });

  const current = filtered[index];

  return (
    <div className="page-container">
      <div className="section-header">
        <h2>{t(lang, 'nav_browse')}</h2>
      </div>

      <div className="search-bar">
        <input
          className="search-input"
          type="search"
          placeholder={t(lang, 'mercs_search')}
          value={query}
          onChange={e => { setQuery(e.target.value); setIndex(0); setShowAd(false); }}
          aria-label="Search missions"
        />
        <select className="search-select" value={category} onChange={e => { setCategory(e.target.value); setIndex(0); setShowAd(false); }} aria-label="Category filter">
          <option value="">All Categories</option>
          {MISSION_CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className="search-select" value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort">
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="filter-tags">
        {tags.map(tag => (
          <button
            key={tag}
            className={`filter-tag ${activeTag === tag ? 'active' : ''}`}
            onClick={() => { setActiveTag(tag); setIndex(0); setShowAd(false); }}
          >{tag}</button>
        ))}
      </div>

      <p className="results-count">{filtered.length} bounties found{filtered.length > 0 ? ` — ${index + 1} / ${filtered.length}` : ''}</p>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <span className="icon">no-results</span>
          <p>{t(lang, 'empty_bounties')}</p>
        </div>
      ) : (
        <div className="swipe-stage" {...swipeHandlers}>
          <button className="swipe-arrow swipe-arrow-left" onClick={goPrev} aria-label="Previous">&lsaquo;</button>

          {showAd ? (
            <div className="swipe-card-wrap ad-card">
              <AdSlot slot="1111111111" format="rectangle" />
              <p className="ad-skip-hint">Tap arrow to continue</p>
            </div>
          ) : (
            <div className="swipe-card-wrap">
              <BountyCard
                mission={current}
                poster={getUserById(current.posterId)}
                onClick={() => setSelectedMission(current)}
              />
            </div>
          )}

          <button className="swipe-arrow swipe-arrow-right" onClick={goNext} aria-label="Next">&rsaquo;</button>
        </div>
      )}

      <MissionDetailModal
        mission={selectedMission}
        open={!!selectedMission}
        onClose={() => setSelectedMission(null)}
        onOpenAuth={() => setAuthOpen(true)}
        onReport={(id, name) => setReportTarget({ id, name })}
      />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <ReportModal
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        targetId={reportTarget ? reportTarget.id : null}
        targetName={reportTarget ? reportTarget.name : undefined}
      />
    </div>
  );
};
